/**
 * RGM Landing search — index build and matching.
 *
 * Deliberately pure and React-free: this is the seam a backend search service replaces later.
 * Today the whole index is derived from the tenant config already in memory, so swapping in an
 * endpoint means changing `useSearchableRoutes` alone, not any component.
 *
 * `screenDetailsWithRoutes` is the source of truth because it is what `<Route>` actually
 * registers and it is the only config block that carries a route-level `label`. `screens` is
 * consulted only to borrow the parent group's icon key, exactly as the Studio cards do.
 */

import { getConfig } from 'utils/helperMethods';

export type RouteSearchEntry = {
    /** `screenDetailsWithRoutes[].id` — unique after de-duplication, and seeds the test id. */
    id: string;
    label: string;
    /** Absolute route, leading slash. */
    url: string;
    /** `STUDIO_ICONS` key inherited from the parent `screens` group; absent renders a neutral tile. */
    icon?: string;
    groupLabel?: string;
};

type GroupMeta = { icon?: string; groupLabel: string };

const readAccessibleScreens = (): string[] | undefined => {
    try {
        const userScope = JSON.parse(localStorage.getItem('userAccessScope') || '{}');
        return userScope?.data_metrics?.forecast_sources?.screens;
    } catch {
        return undefined;
    }
};

/**
 * Same whitelist semantics as `accessibleScreenDetails` in App.tsx — matched on `pageId`, not
 * `id`, because `pageId` is what decides whether the route renders at all. An absent or empty
 * list means no restriction, and iframe demo screens are exempt because no access scope names
 * them.
 */
const isAccessible = (screen: any, accessibleScreens?: string[]): boolean => {
    if (!Array.isArray(accessibleScreens) || accessibleScreens.length === 0) return true;
    return accessibleScreens.includes(screen?.pageId) || Boolean(screen?.iframe?.url);
};

/** `screens` option id -> its parent group's artwork key and label. */
const buildGroupLookup = (screens: any): Record<string, GroupMeta> => {
    const lookup: Record<string, GroupMeta> = {};
    if (!Array.isArray(screens)) return lookup;

    screens.forEach((group) => {
        Object.keys(group?.options || {}).forEach((column) => {
            const entries = group.options[column];
            if (!Array.isArray(entries)) return;
            entries.forEach((entry: any) => {
                if (entry?.id) lookup[entry.id] = { icon: group?.icon, groupLabel: group?.label };
            });
        });
    });

    return lookup;
};

/**
 * Every route the current user can reach, as a flat searchable list.
 *
 * `subRoutes` are left out on purpose: real tenant configs give them only `{ suffix, pageId }`,
 * so App.tsx falls back to the parent's label for them and indexing them would just produce
 * duplicate rows.
 */
export const buildRouteSearchIndex = (appConfig: any): RouteSearchEntry[] => {
    const screenDetails = getConfig('screenDetailsWithRoutes', appConfig);
    if (!Array.isArray(screenDetails)) return [];

    const accessibleScreens = readAccessibleScreens();
    const groupLookup = buildGroupLookup(getConfig('screens', appConfig));
    const byId = new Map<string, RouteSearchEntry>();

    screenDetails.forEach((screen: any) => {
        const { path, id, label } = screen || {};
        if (typeof path !== 'string' || !path || !id || !label) return;
        // Parameterised routes (`.../:mode/:projectId`) are never a search destination
        if (path.includes(':')) return;
        if (!isAccessible(screen, accessibleScreens)) return;

        // Several routes share one id (`psr` sits on the simulator, `/new`, `/edit`, `/view`).
        // The shortest is the landing route, so the winner does not depend on config order.
        const url = `/${path}`;
        const existing = byId.get(id);
        if (existing && existing.url.length <= url.length) return;

        byId.set(id, { id, label, url, ...groupLookup[id] });
    });

    return Array.from(byId.values());
};

/**
 * Case-insensitive substring match on the label, ranked prefix-first then by match position,
 * with the label as a stable tie-break so the order never depends on config order.
 *
 * Omit `limit` for the full result set; the suggestion list passes `MAX_SUGGESTIONS`.
 */
export const matchRoutes = (index: RouteSearchEntry[], query: string, limit?: number): RouteSearchEntry[] => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];

    const ranked = index
        .map((entry) => ({ entry, at: entry.label.toLowerCase().indexOf(needle) }))
        .filter((scored) => scored.at >= 0)
        .sort((a, b) => a.at - b.at || a.entry.label.localeCompare(b.entry.label))
        .map((scored) => scored.entry);

    return typeof limit === 'number' ? ranked.slice(0, limit) : ranked;
};

/**
 * Splits a label for the suggestion row's two-weight rendering: everything up to and including
 * the match is regular, the completion after it is medium. A match that is not a prefix leaves
 * `tail` shorter but still reads correctly, and no match at all renders the label plain.
 */
export const splitLabelOnMatch = (label: string, query: string): { head: string; tail: string } => {
    const needle = query.trim().toLowerCase();
    const at = needle ? label.toLowerCase().indexOf(needle) : -1;
    if (at < 0) return { head: label, tail: '' };

    const end = at + needle.length;
    return { head: label.slice(0, end), tail: label.slice(end) };
};
